// iter-111H — TLDIDialog
//
// Tourism and Leisure Development International (general-contractor)
// file vault. Three buckets:
//   • Weekly Reports
//   • Plans
//   • Documents
//
// Each bucket is a flat list of uploaded files. Admins / engineering /
// contractor users can upload + rename + delete; viewers can browse
// and download. The dialog re-uses the existing `/api/admin/upload`
// pipeline so files automatically benefit from WebP/thumb generation
// and object-storage mirroring.

import { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Upload, FileText, FileArchive, FileBarChart2, Download, Trash2, Pencil, Search, ChevronLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { uploadWithProgress } from '../lib/upload';
import { ADMIN_LIKE_ROLES } from '../lib/permissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  {
    key: 'weekly_reports',
    label: 'Weekly Reports',
    sub: 'Progress reports delivered by TLDI every week',
    icon: FileBarChart2,
    accent: '#003DA5',
  },
  {
    key: 'plans',
    label: 'Plans',
    sub: 'Drawing sets, schedules, construction plans',
    icon: FileArchive,
    accent: '#15803D',
  },
  {
    key: 'documents',
    label: 'Documents',
    sub: 'Contracts, RFIs, change orders & other paperwork',
    icon: FileText,
    accent: '#B45309',
  },
];

function formatBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function TLDIDialog({ open, onClose, adminToken, userRole, userDepartments }) {
  const [category, setCategory] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [uploadProgress, setUploadProgress] = useState(null);
  const fileInputRef = useRef(null);

  const canEdit = useMemo(() => {
    const role = (userRole || '').toLowerCase();
    if (ADMIN_LIKE_ROLES.includes(role)) return true;
    const depts = userDepartments || [];
    return depts.includes('contractor') || depts.includes('engineering');
  }, [userRole, userDepartments]);

  const auth = useMemo(
    () => (adminToken ? { headers: { Authorization: `Bearer ${adminToken}` } } : {}),
    [adminToken],
  );

  // Reset selection when dialog closes.
  useEffect(() => {
    if (!open) {
      setCategory(null);
      setFiles([]);
      setQuery('');
      setUploadProgress(null);
    }
  }, [open]);

  // Load files when a category is opened.
  useEffect(() => {
    if (!open || !category) return;
    setLoading(true);
    axios
      .get(`${API}/tldi/files`, { ...auth, params: { category: category.key } })
      .then(({ data }) => setFiles(Array.isArray(data) ? data : []))
      .catch((err) => toast.error(err?.response?.data?.detail || 'Could not load files'))
      .finally(() => setLoading(false));
  }, [open, category, auth]);

  const filteredFiles = useMemo(() => {
    if (!query.trim()) return files;
    const q = query.toLowerCase();
    return files.filter((f) =>
      (f.title || '').toLowerCase().includes(q) ||
      (f.notes || '').toLowerCase().includes(q),
    );
  }, [files, query]);

  const handleUpload = async (fileList) => {
    if (!fileList || fileList.length === 0 || !category) return;
    for (const file of Array.from(fileList)) {
      try {
        setUploadProgress({ name: file.name, percent: 0 });
        const result = await uploadWithProgress(file, adminToken, (p) =>
          setUploadProgress({ name: file.name, percent: p.percent ?? 0 }),
        );
        // POST the metadata to /api/tldi/files
        const { data } = await axios.post(
          `${API}/tldi/files`,
          {
            category: category.key,
            title: file.name,
            url: result.url,
            ext: result.ext,
            poster_url: result.poster_url || '',
            webp_url: result.webp_url || '',
            thumb_url: result.thumb_url || '',
            file_size: file.size,
          },
          auth,
        );
        setFiles((prev) => [data, ...prev]);
        toast.success(`Uploaded ${file.name}`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || err.message || 'Upload failed');
      }
    }
    setUploadProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRename = async (f) => {
    const next = window.prompt('Rename file', f.title || '');
    if (!next || next === f.title) return;
    try {
      const { data } = await axios.patch(`${API}/tldi/files/${f.id}`, { title: next.trim() }, auth);
      setFiles((prev) => prev.map((x) => (x.id === f.id ? data : x)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rename failed');
    }
  };

  const handleDelete = async (f) => {
    if (!window.confirm(`Delete "${f.title}"?`)) return;
    try {
      await axios.delete(`${API}/tldi/files/${f.id}`, auth);
      setFiles((prev) => prev.filter((x) => x.id !== f.id));
      toast.success('Deleted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="tldi-dialog"
        className="max-w-5xl w-full p-0 overflow-hidden"
        style={{ backgroundColor: '#F8F5EC' }}
      >
        <DialogTitle className="sr-only">TLDI · Tourism and Leisure Development International</DialogTitle>

        {/* Header */}
        <div className="px-6 py-5" style={{ background: 'linear-gradient(135deg, #003DA5 0%, #1F4FBE 100%)', color: '#fff' }}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold opacity-85" style={{ fontFamily: 'Manrope' }}>
                General Contractor
              </p>
              <h2 className="text-2xl font-black mt-0.5" style={{ fontFamily: 'Manrope' }}>TLDI</h2>
              <p className="text-xs opacity-90 mt-0.5" style={{ fontFamily: 'Manrope' }}>
                Tourism and Leisure Development International — file vault
              </p>
            </div>
            <button
              data-testid="tldi-close-btn"
              onClick={onClose}
              className="rounded-full p-1.5 hover:bg-white/10 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {!category ? (
            /* Category picker */
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  data-testid={`tldi-cat-${c.key}`}
                  onClick={() => setCategory(c)}
                  className="text-left p-5 rounded-xl border-2 transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  style={{ borderColor: c.accent, backgroundColor: '#fff' }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                      style={{ backgroundColor: c.accent }}
                    >
                      <c.icon className="w-5 h-5" />
                    </div>
                    <p className="text-base font-bold" style={{ color: '#0A1A2F', fontFamily: 'Manrope' }}>{c.label}</p>
                  </div>
                  <p className="text-xs leading-snug" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>{c.sub}</p>
                </button>
              ))}
            </div>
          ) : (
            /* File list inside a category */
            <>
              <div className="flex items-center justify-between gap-3 mb-4">
                <button
                  data-testid="tldi-back-btn"
                  onClick={() => setCategory(null)}
                  className="inline-flex items-center gap-1 text-sm font-bold hover:opacity-80"
                  style={{ color: category.accent, fontFamily: 'Manrope' }}
                >
                  <ChevronLeft className="w-4 h-4" /> All categories
                </button>
                <h3 className="text-lg font-bold" style={{ color: '#0A1A2F', fontFamily: 'Manrope' }}>
                  {category.label}
                </h3>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    data-testid="tldi-search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by title or notes…"
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border outline-none focus:ring-2"
                    style={{ borderColor: '#E5E7EB', backgroundColor: '#fff', fontFamily: 'Manrope' }}
                  />
                </div>
                {canEdit && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => handleUpload(e.target.files)}
                      data-testid="tldi-file-input"
                    />
                    <button
                      data-testid="tldi-upload-btn"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={!!uploadProgress}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow disabled:opacity-60"
                      style={{ backgroundColor: category.accent, fontFamily: 'Manrope' }}
                    >
                      <Upload className="w-4 h-4" />
                      {uploadProgress ? `Uploading… ${uploadProgress.percent}%` : 'Upload'}
                    </button>
                  </>
                )}
              </div>

              {loading ? (
                <p className="text-center py-12 text-sm" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
                  Loading…
                </p>
              ) : filteredFiles.length === 0 ? (
                <div className="text-center py-12 rounded-xl border-2 border-dashed" style={{ borderColor: '#CBD5E1' }}>
                  <p className="text-sm font-bold mb-1" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
                    {files.length === 0 ? 'No files yet' : 'No matches'}
                  </p>
                  <p className="text-xs" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                    {files.length === 0 && canEdit ? 'Click Upload to add your first file.' : 'Try a different search term.'}
                  </p>
                </div>
              ) : (
                <ul className="space-y-2" data-testid="tldi-file-list">
                  {filteredFiles.map((f) => {
                    const fileUrl = f.url?.startsWith('http') ? f.url : `${process.env.REACT_APP_BACKEND_URL}${f.url}`;
                    return (
                      <li
                        key={f.id}
                        data-testid={`tldi-file-${f.id}`}
                        className="flex items-center gap-3 p-3 rounded-lg bg-white border hover:shadow-sm transition-shadow"
                        style={{ borderColor: '#E5E7EB' }}
                      >
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0"
                          style={{ backgroundColor: category.accent }}
                        >
                          <category.icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold truncate" style={{ color: '#0A1A2F', fontFamily: 'Manrope' }}>
                            {f.title || 'Untitled'}
                          </p>
                          <p className="text-[11px]" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                            {[
                              formatDate(f.created_at),
                              f.created_by_name || f.created_by,
                              formatBytes(f.file_size),
                              (f.ext || '').toUpperCase(),
                            ].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          download={f.title}
                          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                          title="Download / Open"
                          data-testid={`tldi-download-${f.id}`}
                        >
                          <Download className="w-4 h-4" style={{ color: category.accent }} />
                        </a>
                        {canEdit && (
                          <>
                            <button
                              onClick={() => handleRename(f)}
                              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                              title="Rename"
                              data-testid={`tldi-rename-${f.id}`}
                            >
                              <Pencil className="w-4 h-4" style={{ color: '#4A5D5D' }} />
                            </button>
                            <button
                              onClick={() => handleDelete(f)}
                              className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                              title="Delete"
                              data-testid={`tldi-delete-${f.id}`}
                            >
                              <Trash2 className="w-4 h-4" style={{ color: '#DC2626' }} />
                            </button>
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
